import Vue from 'vue'
import VueCompositionApi, {
	reactive,
	toRefs,
	computed,
} from '@vue/composition-api'
Vue.use(VueCompositionApi)

const customerSpecialPricesState = reactive({
	customersData: [],
	filteredCustomers: [],
	loading: false,
	error: null,
	// Modal state
	modalVisible: false,
	selectedCustomer: null,
	// Mutation states
	isUpdating: false,
	isDeleting: false,
	isAdding: false,
	mutationError: null,
	customersColumns: [
		{
			label: 'Customer ID',
			field: 'customer_id',
			sortable: true,
		},
		{
			label: 'Customer',
			field: 'customer_display',
			sortable: true,
		},
		{
			label: 'View',
			field: 'view',
			width: '100px',
			sortable: false,
		},
	],
	customersRows: computed(() => {
		return customerSpecialPricesState.filteredCustomers.map((customer) => ({
			customer_id: customer.customer_id,
			customer_display: `${customer.customer_title} - ${
				customer.customer_nickname || ''
			}`.trim(),
			view: 'view',
			original_data: customer,
		}))
	}),
})

export default function useCustomerSpecialPrices() {
	const setCustomersData = (customers) => {
		if (customers && customers.customers) {
			customerSpecialPricesState.customersData = customers.customers
			filterCustomersWithSpecialPrices()
		}
	}

	const filterCustomersWithSpecialPrices = () => {
		customerSpecialPricesState.filteredCustomers =
			customerSpecialPricesState.customersData.filter(
				(customer) =>
					customer.customer_price_lists &&
					customer.customer_price_lists.length > 0,
			)
	}

	const setLoading = (loading) => {
		customerSpecialPricesState.loading = loading
	}

	const setError = (error) => {
		customerSpecialPricesState.error = error
	}

	const getCustomerById = (customerId) => {
		return customerSpecialPricesState.filteredCustomers.find(
			(customer) => customer.customer_id === customerId,
		)
	}

	// Modal functions
	const openModal = (customer) => {
		customerSpecialPricesState.selectedCustomer = customer
		customerSpecialPricesState.modalVisible = true
	}

	const closeModal = () => {
		customerSpecialPricesState.modalVisible = false
		customerSpecialPricesState.selectedCustomer = null
		customerSpecialPricesState.mutationError = null
	}

	// Update customer's special price in local state
	const updateLocalPrice = (customerId, stockId, newPrice) => {
		const customer = customerSpecialPricesState.filteredCustomers.find(
			(c) => c.customer_id === customerId,
		)
		if (customer && customer.customer_price_lists) {
			const priceItem = customer.customer_price_lists.find(
				(p) => p.stock.stock_id === stockId,
			)
			if (priceItem) {
				priceItem.customers_price = newPrice
			}
		}
		// Update selected customer if it's the same
		if (
			customerSpecialPricesState.selectedCustomer &&
			customerSpecialPricesState.selectedCustomer.customer_id === customerId
		) {
			customerSpecialPricesState.selectedCustomer = { ...customer }
		}
	}

	// Delete customer's special price from local state
	const deleteLocalPrice = (customerId, stockId) => {
		const customer = customerSpecialPricesState.filteredCustomers.find(
			(c) => c.customer_id === customerId,
		)
		if (customer && customer.customer_price_lists) {
			customer.customer_price_lists = customer.customer_price_lists.filter(
				(p) => p.stock.stock_id !== stockId,
			)
			// If no more special prices, remove from filtered list
			if (customer.customer_price_lists.length === 0) {
				customerSpecialPricesState.filteredCustomers =
					customerSpecialPricesState.filteredCustomers.filter(
						(c) => c.customer_id !== customerId,
					)
				// Close modal if this customer was removed
				if (
					customerSpecialPricesState.selectedCustomer &&
					customerSpecialPricesState.selectedCustomer.customer_id === customerId
				) {
					closeModal()
				}
			} else if (
				customerSpecialPricesState.selectedCustomer &&
				customerSpecialPricesState.selectedCustomer.customer_id === customerId
			) {
				customerSpecialPricesState.selectedCustomer = { ...customer }
			}
		}
	}

	// Add new special price to local state
	const addLocalPrice = (customerId, priceData) => {
		const customer = customerSpecialPricesState.customersData.find(
			(c) => c.customer_id === customerId,
		)
		if (customer) {
			if (!customer.customer_price_lists) {
				customer.customer_price_lists = []
			}
			customer.customer_price_lists.push(priceData)
			// Re-filter to ensure customer appears in filtered list
			filterCustomersWithSpecialPrices()
			// Update selected customer if it's the same
			if (
				customerSpecialPricesState.selectedCustomer &&
				customerSpecialPricesState.selectedCustomer.customer_id === customerId
			) {
				customerSpecialPricesState.selectedCustomer = { ...customer }
			}
		}
	}

	// Mutation state setters
	const setIsUpdating = (value) => {
		customerSpecialPricesState.isUpdating = value
	}

	const setIsDeleting = (value) => {
		customerSpecialPricesState.isDeleting = value
	}

	const setIsAdding = (value) => {
		customerSpecialPricesState.isAdding = value
	}

	const setMutationError = (error) => {
		customerSpecialPricesState.mutationError = error
	}

	return {
		...toRefs(customerSpecialPricesState),
		setCustomersData,
		filterCustomersWithSpecialPrices,
		setLoading,
		setError,
		getCustomerById,
		// Modal functions
		openModal,
		closeModal,
		// Local state update functions
		updateLocalPrice,
		deleteLocalPrice,
		addLocalPrice,
		// Mutation state setters
		setIsUpdating,
		setIsDeleting,
		setIsAdding,
		setMutationError,
	}
}
